import _, { memoize } from 'lodash';
import numeral from 'numeral';
import {
  CONFIG,
  DONATION_COMPARE,
  DONATION_TYPES,
  METRICS,
  SEGMENTS,
  TYPES,
  IMPACT_METRICS_FIELD,
  getFilterYear,
  getFilters
} from './constants';

/*
  Util used to group data by provided portfolio
  params (data: array)
*/
const groupByPortfolio = data => {
  return _(data)
    .groupBy('Portfolios.id')
    .map((objs, key) => {
      return {
        ...objs[0],
        'Portfolios.id': key,
        'Results.forecastCurrent': _.sumBy(objs, 'Results.forecastCurrent')
      };
    })
    .value();
};

/*
  Util used to group data by provided segment
  params (data: array, segmentRowName: string, metricRowName: string)
*/
const groupBySegment = (data, segmentRowName, metricRowName) => {
  const sharedRowName = {};
  Object.keys(data).forEach(key => {
    const grouped = data[key].reduce((res, obj) => {
      const id = obj[segmentRowName];
      if (id in res) {
        res[id][metricRowName] += obj[metricRowName];
      } else {
        res[id] = { ...obj };
      }
      return res;
    }, {});
    sharedRowName[key] = Object.values(grouped);
  });
  return sharedRowName;
};

/*
  Parses beneficiary card results, it expects the cube response data and card configuration
  params (
    data: array,
    card: object,
    beneficiarieList: array,
    isComparisonCard: boolean,
    total: object
  )
*/
const parseBeneficiaries = (
  data,
  card,
  beneficiarieList,
  isComparisonCard,
  totals
) => {
  let rows = [...data];
  const getDemographicTagNameByKey = key => {
    return beneficiarieList.filter(item => item.id === parseInt(key, 10))[0]
      .name;
  };
  const { reports } = totals;
  const {
    settings: {
      metric = 'Report Count',
      segment = 'NPO',
      comparisonyear = null
    }
  } = card;
  let beneficiaries = card.settings.beneficiaries || [];
  if (card.settings.beneficiaries.includes(0)) {
    beneficiaries = beneficiarieList.map(b => b.id);
  }
  const includeComparisonYear =
    comparisonyear === 'None' ? null : comparisonyear;

  if (isComparisonCard && !includeComparisonYear) {
    return [];
  }
  if (!isComparisonCard && includeComparisonYear) {
    rows = data.filter(
      row => row['Reports.year'] !== parseInt(includeComparisonYear, 10)
    );
  }
  if (isComparisonCard && includeComparisonYear) {
    rows = data.filter(
      row => row['Reports.year'] === parseInt(includeComparisonYear, 10)
    );
  }

  const { valueRow, name } = CONFIG[TYPES.BENEFICIARIES][metric];
  let emptyMessage = null;

  const total =
    metric === METRICS.REPORT
      ? reports
      : rows.reduce((sum, row) => {
          return sum + row[valueRow];
        }, 0);

  let filterDemographic = rows.filter(item =>
    beneficiaries.includes(item['BenficiaryDemographics.demographicId'])
  );

  const containsAnyDemographic = !!card.settings.switch;

  /*
    This condition is triggered when the toggle is ALL
    it filters reports that only contains all specified
    demographic tags
  */
  if (!containsAnyDemographic && beneficiaries.length) {
    const tempDemographicRow = [];
    const sharedPortfolio = _.groupBy(filterDemographic, 'Portfolios.id');
    Object.keys(sharedPortfolio).forEach(key => {
      if (
        sharedPortfolio[key] &&
        sharedPortfolio[key].length === beneficiaries.length
      ) {
        sharedPortfolio[key].forEach(row => {
          const currentIndex = _.findIndex(tempDemographicRow, {
            'Portfolios.id': row['Portfolios.id']
          });
          if (currentIndex > -1) {
            tempDemographicRow[currentIndex][valueRow] += row[valueRow];
          } else {
            const title = beneficiaries
              .map(tagId => getDemographicTagNameByKey(tagId))
              .join(', ');
            const firstTag = beneficiaries[0];
            tempDemographicRow.push({
              ...row,
              title,
              'BenficiaryDemographics.demographicId': firstTag
            });
          }
        });
      }
    });
    filterDemographic = [...tempDemographicRow];
    if (filterDemographic.length === 0) {
      emptyMessage = `You have "All" selected, and no reports contain all ${beneficiaries.length} beneficiaries.  Try selecting other ` +
        'years, selecting fewer beneficiaries, or change to "Any" mode, which will include reports that contain any of the ' +
        'selected beneficiaries.';
    }
  }

  const dataReady =
    metric === METRICS.BENEFICIARIES
      ? groupByPortfolio(filterDemographic)
      : filterDemographic;

  const groupedByDemographics = _.groupBy(
    dataReady,
    'BenficiaryDemographics.demographicId'
  );

  const { rowName } = SEGMENTS[segment];

  const groupedBySegments = groupBySegment(
    groupedByDemographics,
    rowName,
    valueRow
  );

  const blocks = Object.keys(groupedBySegments).map(key => {
    const totalBlock = groupedBySegments[key].reduce((sum, row) => {
      return sum + row[valueRow];
    }, 0);
    const dataElements = groupedBySegments[key].map(row => ({
      name: row[rowName] || '-',
      value: row[valueRow],
      color: '#ff9800',
      percentage: `${((row[valueRow] / totalBlock) * 100).toFixed(1)}%`
    }));
    const demographicName = groupedBySegments[key][0].title
      ? groupedBySegments[key][0].title
      : getDemographicTagNameByKey(key);
    return {
      name: demographicName,
      total: totalBlock,
      dataElements,
      demographicId: parseInt(key, 10),
      percentage: `${((totalBlock / total) * 100).toFixed(1)}%`
    };
  });

  let parsedYears = 'all years';
  let parsedBeneficiaries = 'all beneficiaries';
  let titleParsedYears = '';

  const { title } = getFilterYear(card.settings.filter);

  if (title) {
    titleParsedYears = title;
  }

  if (card.settings && card.settings.filter) {
    const [py] = card.settings.year;
    parsedYears = py;
  }
  if (card.settings && card.settings.year && card.settings.year.length > 0) {
    if (parsedYears === 'all years') {
      parsedYears = '';
    }
    if (parsedYears.length > 0) {
      parsedYears += ', ';
    }
    parsedYears += card.settings.year.join(', ');
  }
  if (isComparisonCard && includeComparisonYear) {
    parsedYears = includeComparisonYear;
  }
  if (!isComparisonCard && includeComparisonYear) {
    const [py] = card.settings.year;
    parsedYears = py;
  }
  if (
    card.settings.comparisonyear.length > 0 &&
    card.settings.comparisonyear !== 'No Comparison'
  ) {
    if (card.settings.year[0] === undefined) {
      titleParsedYears = 'No Year';
    } else {
      titleParsedYears = `${card.settings.year[0]}, ${card.settings.comparisonyear}`;
    }
  }

  parsedBeneficiaries = beneficiaries
    .map(tagId => getDemographicTagNameByKey(tagId))
    .join(', ');
  const parsedName = name
    .replace('[TOTAL]', numeral(total).format(0, 0))
    .replace('[YEARS]', titleParsedYears)
    .replace('[TAGS]', parsedBeneficiaries);

  beneficiaries.forEach(beneficiary => {
    const tag = getDemographicTagNameByKey(beneficiary);
    const newTag = blocks.filter(block => block.name === tag).length === 0;
    if (newTag) {
      blocks.push({
        name: tag,
        total: 0,
        dataElements: [],
        percentage: '0%'
      });
    }
  });

  return {
    total,
    name: parsedName,
    blocks,
    emptyMessage,
    years: parsedYears
  };
};

/*
  Parses donation card results, it expects the cube response data and card configuration
  params (data: array, card: object)
*/
const parseDonations = (data, card) => {
  const rows = [];
  const total = data.reduce((amount, item) => {
    return amount + item['Investments.totalAmount'];
  }, 0);
  const {
    settings: { donationType, filter, segment, compare }
  } = card;

  const currentYear = new Date().getFullYear();
  const valueField = DONATION_TYPES[donationType];
  const { filterYear, title } = getFilterYear(filter);
  const { rowName } = SEGMENTS[segment];

  const buildYearRow = (yearData, baseTitle) => {
    return yearData.reduce(
      (row, item, idx) => {
        const rawTotal = valueField ? item[valueField] :
          (
            item['Investments.cashAmount'] +
            item['Investments.goodsAmount'] +
            item['Investments.servicesAmount'] +
            item['Investments.volunteerismAmount']
          );

        row.total += rawTotal;
        if (item[rowName]) {
          row.segments.push({
            title: `${item[rowName]}`,
            total: `$${numeral(rawTotal).format(0, 0)}`,
            rawTotal,
            data: [
              { title: 'Cash', value: item['Investments.cashAmount'] },
              { title: 'Goods', value: item['Investments.goodsAmount'] },
              { title: 'Services', value: item['Investments.servicesAmount'] },
              {
                title: 'Volunteerism',
                value: item['Investments.volunteerismAmount']
              }
            ]
          });
        }
        if (yearData.length === idx + 1) {
          row.title += ` - $${numeral(row.total).format(
            0,
            0
          )} - (${donationType})`;
        }
        return row;
      },
      {
        title: baseTitle,
        total: 0,
        segments: []
      }
    );
  };

  const mainData = filterYear
    ? data.filter(item => item['Portfolios.year'] === filterYear)
    : [...data];

  let compareYear = null;

  if (compare === DONATION_COMPARE.LAST_YEAR || (typeof compare === 'number')) {
    {/* eslint-disable-next-line */}
    const yearTocompare = (typeof compare === 'number') ? compare : currentYear - 1;
    const compareData = data.filter(
      item => item['Portfolios.year'] === yearTocompare
    );
    compareYear = buildYearRow(compareData, yearTocompare);
  }

  const mainYear = buildYearRow(mainData, title);

  const yearsObj = data.reduce((acc, item) => {
    {/* eslint-disable-next-line */}
    if (acc.hasOwnProperty(item['Portfolios.year'])) {
      acc[item['Portfolios.year']] += item['Investments.totalAmount'];
    } else {
      acc[item['Portfolios.year']] = item['Investments.totalAmount'];
    }
    return acc;
  }, {});
  const years = Object.keys(yearsObj).map(year => {
    return { title: year, value: yearsObj[year] };
  });

  if (mainYear) rows.push(mainYear);
  if (compareYear) rows.push(compareYear);
  return { total, rows, years };
};

/*
  Parses reporting card results, it expects the cube response data and card configuration
  params (data: array, card: object)
*/
const parseReporting = (data, card) => {
  const { segment, filter } = card.settings;
  const completedStatus = ['Finalized', 'Published'];
  const { rowName } = SEGMENTS[segment];
  const currentYear = new Date().getFullYear();
  let years = data.reduce((acc, row) => {
    const year = row['Reports.year'];
    if (year !== null && acc.indexOf(year) === -1) acc.push(year);
    return acc;
  }, []);
  if (filter === 'This Year') {
    years = [`${currentYear}`];
  }
  const generalData = data.reduce(
    (acc, row) => {
      const generalIndex = _.findIndex(acc.general, i => {
        return i.status === row['Reports.status'];
      });
      const dataElement = {
        status: row['Reports.status'],
        total: row['Reports.count']
      };
      if (generalIndex > -1) {
        acc.general[generalIndex].total += row['Reports.count'];
      } else {
        acc.general.push(dataElement);
      }
      acc.total += row['Reports.count'];
      if (completedStatus.includes(row['Reports.status'])) {
        acc.completed += row['Reports.count'];
      }
      return acc;
    },
    { total: 0, completed: 0, general: [] }
  );
  const reduceYearData = yearData => {
    const reducedData = yearData.reduce((acc, row) => {
      const segmentIndex = _.findIndex(acc, i => {
        return i.title === row[rowName];
      });
      const dataElement = {
        status: row['Reports.status'],
        total: row['Reports.count']
      };
      if (segmentIndex > -1) {
        acc[segmentIndex].data.push(dataElement);
      } else {
        acc.push({ title: row[rowName], data: [dataElement] });
      }
      return acc;
    }, []);
    return reducedData.map(row => {
      return {
        ...row,
        data: row.data.reduce((acc, item) => {
          const statusIndex = _.findIndex(acc, i => {
            return i.status === item.status;
          });
          if (statusIndex > -1) {
            acc[statusIndex].total = (acc[statusIndex].total + item.total);
          } else {
            acc.push(item);
          }
          return acc;
        }, [])
      };
    });
  };
  const results = { ...generalData, yearsData: [] };
  years.forEach(year => {
    const yearData = data.filter(
      row => row['Portfolios.year'] === parseInt(year, 10)
    );
    if (yearData && yearData.length > 0) {
      const rows = reduceYearData(yearData);
      results.yearsData.push({ title: year, rows });
    }
  });
  return results;
};

/*
  Parses data quality card results, it expects the cube response data and card configuration
  params (data: array, card: object)
*/
const parseDataQuality = (data, card) => {
  const { segment, filter } = card.settings;
  const { rowName } = SEGMENTS[segment];
  const currentYear = new Date().getFullYear();
  let years = data.reduce((acc, row) => {
    const year = row['Reports.year'];
    if (year !== null && acc.indexOf(year) === -1) acc.push(year);
    return acc;
  }, []);
  if (filter === 'This Year') {
    years = [`${currentYear}`];
  }
  const reduceYearData = yearData => {
    return yearData.reduce((acc, row) => {
      const segmentIndex = _.findIndex(acc, i => {
        return i.title === row[rowName];
      });
      if (segmentIndex > -1) {
        acc[segmentIndex].total += 1;
        acc[segmentIndex].nonGuess += row['Reports.nonGuess'];
      } else {
        acc.push({ title: row[rowName], total: 1, nonGuess: row['Reports.nonGuess'] });
      }
      return acc;
    }, []);
  };
  const results = { 
    total: data.length,
    nonGuess: data.filter(row => row['Reports.nonGuess']).length,
    yearsData: []
  };
  years.forEach(year => {
    const yearData = data.filter(
      row => row['Portfolios.year'] === parseInt(year, 10)
    );
    const rows = reduceYearData(yearData);
    results.yearsData.push({ title: year, rows });
  });
  return results;
};

/*
  Parses impact card results, it expects the cube response data and card configuration
  params (data: array, card: object)
*/
const parseImpacts = (data, card) => {
  const { segment, metric, catalized } = card.settings;
  const cardMetric =  (metric === 'Total claim' && catalized) ? 'Total claim Catalized' : metric;
  const agrupator = SEGMENTS[segment].rowName;
  const valueField = IMPACT_METRICS_FIELD[cardMetric];
  const grouped = _.chain(data)
    .groupBy(agrupator)
    .map((value, key) => ({ [agrupator]: key, rows: value }))
    .value().filter(group => group[agrupator] !== 'null');
  const headers = [
    ...grouped.map(group => group[agrupator]),
  ];
  const groupByIndicator = _.chain(data)
    .groupBy('Indicators.name')
    .map((value, key) => ({ 'Indicators.name': key, rows: value }))
    .value().filter(group => group['Indicators.name'] !== 'null');

  const rows = groupByIndicator.map(row => {
    const nextRows = {
      'Indicators': row['Indicators.name'],
    };
    headers.forEach(header => {
      const compare = (agrupator === 'Portfolios.year') ? parseInt(header) : header;
      const col = row.rows.filter(item => (item[agrupator] === compare));
      const value = col.length > 0 ? col[0][valueField] : 0;
      if (cardMetric === 'Leverage') {
        nextRows[header] = numeral((value || 0)).format('0.00') + '%';
      } else {
        nextRows[header] = numeral(value || 0).format('0,00');
      }
    });
    return nextRows;
  });
  return { headers: ['Indicators', ...headers], rows };
};

/*
  Parses cube server response data, to in-app format, expected by cards
  params (
    resultSet: array,
    card: objects, beneficiaries: list,
    isComparisonCard: boolean,
    totals: object
  )
*/
export const parseCubeData = memoize(({
  resultSet,
  card,
  beneficiaries,
  isComparisonCard = false,
  totals
}) => {
  const parseBeneficiariesMemoized = memoize(parseBeneficiaries);
  const parseDonationsMemoized = memoize(parseDonations);
  const parseReportingMemoized = memoize(parseReporting);
  const perseDataQualityMemoized = memoize(parseDataQuality);
  const perseImpactsMemoized = memoize(parseImpacts);

  if (
    resultSet &&
    beneficiaries &&
    beneficiaries.length > 0
  ) {
    switch (card.title) {
      case TYPES.BENEFICIARIES:
        return parseBeneficiariesMemoized(
          resultSet,
          card,
          beneficiaries,
          isComparisonCard,
          totals
        );
      case TYPES.DONATIONS:
        if (isComparisonCard) return null;
        return parseDonationsMemoized(resultSet, card);
      case TYPES.REPORTING:
        if (isComparisonCard) return null;
        return parseReportingMemoized(resultSet, card);
      case TYPES.DATA:
        if (isComparisonCard) return null;
        return perseDataQualityMemoized(resultSet, card);
        case TYPES.IMPACTS:
          if (isComparisonCard) return null;
          return perseImpactsMemoized(resultSet, card);
      default:
        return null;
    }
  }
  return null;
});

/*
  Parses a list of details data to the csv exportable expected output
  params (detailsList: array, impactDetails: array, title: string)
*/
export const parseDetailListToCsv = memoize((detailsList, impactDetails, title) => {
  const data = [];
  let headers = [];

  if (title === 'Impacts' && impactDetails) {
    headers = [
      { label: 'Report', key: 'report' },
      { label: 'Indicator', key: 'indicator' },
      { label: 'Total claim', key: 'total_claim' },
      { label: 'SROI', key: 'sroi' },
      { label: 'Leverage', key: 'leverage' },
      { label: 'Allocations', key: 'allocation' }
    ];
    impactDetails.forEach(npoRow => {
      npoRow.reports.forEach(report => {
        data.push({
          report: report.report,
          indicator: report.indicator,
          total_claim: `$${numeral(report.total_claim).format(0, 0)}`,
          sroi: `$${numeral(report.sroi).format(0, 0)}`,
          leverage: `${numeral(report.leverage).format('0.0')}%`,
          allocation: `$${numeral(report.allocation).format(0, 0)}`,
        });
      });
    });
  } else {
    switch (title) {
      case 'Data Quality':
        headers = [
          { label: 'NPO / Report', key: 'name' },
          { label: 'Result Count', key: 'relevantResults' },
          { label: 'G Results Count', key: 'gResults' },
          { label: 'M Results Count', key: 'mResults' },
          { label: 'E Results Count', key: 'eResults' }
        ];

        detailsList.forEach(npo => {
          data.push({
            name: npo.npo,
            relevantResults: numeral(npo.relevantResults).format(0, 0),
            gResults: numeral(npo.gResults).format(0, 0),
            mResults: numeral(npo.mResults).format(0, 0),
            eResults: numeral(npo.eResults).format(0, 0), 
          });
          npo.details.forEach(detail => {
            data.push({
              name: detail.report,
              relevantResults: numeral(detail.relevantResults).format(0, 0),
              gResults: numeral(detail.gResults).format(0, 0),
              mResults: numeral(detail.mResults).format(0, 0),
              eResults: numeral(detail.eResults).format(0, 0),
            });
          });
        });

        break;
      case 'Beneficiaries':
        headers = [
          { label: 'NPO / Report', key: 'name' },
          { label: 'Reach', key: 'reach' },
          { label: 'Allocation', key: 'allocation' },
          { label: 'Measurement', key: 'measurement' }
        ];
    
        detailsList.forEach(npo => {
          data.push({
            name: npo.npo,
            reach: numeral(npo.reach).format(0, 0),
            allocation: `$${numeral(npo.allocation).format(0, 0)}`,
            measurement: `${numeral(npo.measurement / npo.details.length).format(
              '0,0'
            )}%`
          });
          npo.details.forEach(detail => {
            data.push({
              name: detail.report,
              reach: numeral(detail.reach).format(0, 0),
              allocation: `$${numeral(detail.allocation).format(0, 0)}`,
              measurement: `${numeral(detail.measurement).format('0,0')}%`
            });
          });
        });
        break;
      case 'Reporting Compliance':
        headers = [
          { label: 'NPO / Report', key: 'name' },
          { label: 'Status', key: 'status' },
          { label: 'Last Update', key: 'lastUpdate' },
        ];

        detailsList.forEach(npo => {
          data.push({
            name: npo.npo,
            status: numeral(npo.reach).format(0, 0),
            lastUpdate: new Date(npo.lastUpdate),
          });
          npo.details.forEach(detail => {
            data.push({
              name: detail.report,
              status: detail.status,
              lastUpdate: new Date(detail.lastUpdate),
            });
          });
        });
        break;
      case 'Donations':
        headers = [
          { label: 'NPO / Report', key: 'name' },
          { label: 'Reach', key: 'reach' },
          { label: 'Allocation', key: 'allocation' },
          { label: 'Measurement', key: 'measurement' }
        ];
    
        detailsList.forEach(npo => {
          data.push({
            name: npo.npo,
            reach: numeral(npo.reach).format(0, 0),
            allocation: `$${numeral(npo.allocation).format(0, 0)}`,
            measurement: `${numeral(npo.measurement / npo.details.length).format(
              '0,0'
            )}%`
          });
          npo.details.forEach(detail => {
            data.push({
              name: detail.report,
              reach: numeral(detail.reach).format(0, 0),
              allocation: `$${numeral(detail.allocation).format(0, 0)}`,
              measurement: `${numeral(detail.measurement).format('0,0')}%`
            });
          });
        });    
        break;
    }
  }
  return {
    headers,
    data
  };
});
